import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, unwrapObject } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

interface Mine {
  today: { attempts: number; connected: number; contact_rate_pct: number; talk_sec: number; avg_talk_sec: number; target_daily_calls?: number };
  best_day_attempts: number;
  need_outcome: number;
  month: { period: string; connected: number; talk_hours: number; target_connected_calls: number; target_talk_hours: number; elapsed_pct: number; days_left: number };
}

const mins = (s: number) => (s >= 3600 ? `${Math.floor(s / 3600)} h ${Math.floor((s % 3600) / 60)} min` : `${Math.floor(s / 60)} min`);

/** Where a rep is against an even pace through the month, said in words as well as colour */
function pace(done: number, target: number, elapsed: number) {
  const pct = (done / target) * 100;
  if (pct >= 100) return { label: "Target met", tone: "text-success", bar: "bg-success" };
  if (pct >= elapsed + 5) return { label: "Ahead of pace", tone: "text-success", bar: "bg-success" };
  if (pct >= elapsed - 10) return { label: "On pace", tone: "text-muted-foreground", bar: "bg-primary" };
  return { label: "Behind pace", tone: "text-warning", bar: "bg-warning" };
}

function Meter({ label, done, target, unit, elapsed }: { label: string; done: number; target: number; unit: string; elapsed: number }) {
  const p = pace(done, target, elapsed);
  const width = Math.min(100, (done / target) * 100);
  return (
    <div className="min-w-[220px] flex-1 space-y-1">
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span className="font-medium text-foreground">{label}</span>
        <span className="font-mono text-muted-foreground">{done}{unit} of {target}{unit}</span>
      </div>
      <div className="relative h-2 rounded-full bg-border/70" role="img" aria-label={`${Math.round(width)}% of target, ${Math.round(elapsed)}% of the month gone`}>
        <div className={cn("absolute inset-y-0 left-0 rounded-full", p.bar)} style={{ width: `${width}%` }} />
        <div className="absolute -inset-y-1 w-0.5 bg-foreground" style={{ left: `${Math.min(100, elapsed)}%` }} />
      </div>
      <div className={cn("text-[11.5px] font-semibold", p.tone)}>{p.label}</div>
    </div>
  );
}

/** A rep's calling today and against this month's call targets; hidden for people who can't call */
export function CallingToday() {
  const { hasPermission } = useAuth();
  const allowed = hasPermission("call.make");
  const [m, setM] = useState<Mine | null>(null);
  const load = useCallback(async () => {
    try {
      setM(unwrapObject<Mine>(await api.get("/api/Report/CallsMine")));
    } catch {
      setM(null);
    }
  }, []);
  useEffect(() => {
    if (!allowed) return;
    void load();
    window.addEventListener("crm:call-saved", load);
    return () => window.removeEventListener("crm:call-saved", load);
  }, [allowed, load]);

  if (!allowed || !m) return null;
  if (m.today.attempts === 0 && !m.today.target_daily_calls && m.month.connected === 0 && !m.month.target_connected_calls && !m.month.target_talk_hours && !m.need_outcome) return null;
  const t = m.today;
  const mo = m.month;
  return (
    <section aria-label="Your calls" className="space-y-3 border-b border-border px-4 py-3 md:px-6">
      <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
        <Tile label="Calls today" value={t.target_daily_calls ? `${t.attempts} / ${t.target_daily_calls}` : String(t.attempts)} sub={t.target_daily_calls ? (t.attempts >= t.target_daily_calls ? "Daily target met" : `${t.target_daily_calls - t.attempts} more for today's target`) : m.best_day_attempts > t.attempts ? `Best day this week: ${m.best_day_attempts}` : t.attempts ? "Your best day this week" : "No calls yet"} />
        <Tile label="Connected" value={String(t.connected)} sub={`${t.contact_rate_pct}% contact rate`} />
        <Tile label="Talk time" value={mins(t.talk_sec)} sub={t.connected ? `Average call ${mins(t.avg_talk_sec)}` : "—"} />
        <Tile label="Need an outcome" value={String(m.need_outcome)} sub={m.need_outcome ? "Open the call panel to add them" : "All saved"} warn={m.need_outcome > 0} />
      </div>
      {(mo.target_connected_calls > 0 || mo.target_talk_hours > 0) && (
        <div className="space-y-2">
          <div className="flex items-baseline justify-between text-xs text-muted-foreground">
            <span>This month's call targets · the line is where an even pace would be · {mo.days_left} day{mo.days_left === 1 ? "" : "s"} left</span>
            <Link to="/targets" className="text-primary hover:underline">All targets</Link>
          </div>
          <div className="flex flex-wrap gap-5">
            {mo.target_connected_calls > 0 && <Meter label="Connected calls" done={mo.connected} target={mo.target_connected_calls} unit="" elapsed={mo.elapsed_pct} />}
            {mo.target_talk_hours > 0 && <Meter label="Talk time" done={mo.talk_hours} target={mo.target_talk_hours} unit=" h" elapsed={mo.elapsed_pct} />}
          </div>
        </div>
      )}
    </section>
  );
}

function Tile({ label, value, sub, warn }: { label: string; value: string; sub: string; warn?: boolean }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-xl border border-border bg-card px-3.5 py-2.5">
      <span className="text-[11.5px] text-muted-foreground">{label}</span>
      <span className="font-mono text-xl font-semibold">{value}</span>
      <span className={cn("text-[11.5px]", warn ? "text-warning" : "text-muted-foreground")}>{sub}</span>
    </div>
  );
}
